<template>
  <div :class="$attrs.class" :style="$attrs.style as any">
    <div v-if="props.label" class="select-label" :class="{ 'is-required': props.required }">
      {{ props.label }}
      <span v-if="props.required" class="required-indicator">*</span>
    </div>
    <div class="entity-select-container" ref="containerRef" v-bind="filteredAttrs">
      <div
        ref="triggerRef"
        class="select-trigger"
        :class="{
          'is-open': isOpen,
          'has-error': Array.isArray(errors) ? errors.length > 0 : !!errors,
          'has-entity': getOptionInitials(selectedOption) || selectedOption?.image,
        }"
        @click="toggleDropdown"
        tabindex="0"
        @keydown.space.prevent="toggleDropdown"
        @keydown.enter.prevent.stop="toggleDropdown"
        @keydown.esc="closeDropdown"
        @keydown.tab="closeDropdown">
        <div class="trigger-content">
          <template v-if="selectedOption">
            <div v-if="selectedOption.image || getOptionInitials(selectedOption)" class="entity-wrapper">
              <img v-if="selectedOption.image" :src="selectedOption.image" alt="entity icon" class="entity-image" />
              <div v-else-if="getOptionInitials(selectedOption)" class="entity-initials">
                {{ getOptionInitials(selectedOption) }}
              </div>
              <div v-else class="entity-initials fallback">?</div>
            </div>
            <span class="selected-label">{{ selectedOption.label }}</span>
          </template>

          <span v-else class="placeholder-text">{{ placeholder }}</span>
        </div>

        <IconChevronDown class="chevron-icon" :class="{ 'rotate-180': isOpen }" size="16" />
      </div>

      <transition name="fade">
        <div v-if="isOpen" class="options-dropdown">
          <ul v-if="options.length > 0" class="options-list">
            <li
              v-for="option in options"
              :key="option.value"
              class="option-item"
              :class="{
                'is-selected': modelValue === option.value,
                'has-entity': getOptionInitials(option) || option?.image,
              }"
              @click="selectOption(option)">
              <div v-if="option.image || getOptionInitials(option)" class="entity-wrapper">
                <img v-if="option.image" :src="option.image" class="entity-image" />
                <div v-else-if="getOptionInitials(option)" class="entity-initials">
                  {{ getOptionInitials(option) }}
                </div>
              </div>
              <span>{{ option.label }}</span>
            </li>
          </ul>
          <NoResults v-else :message="props.emptyMessage">
            <template v-if="$slots['no-results']" #no-results="{ searchQuery: sq }">
              <slot name="no-results" :searchQuery="sq" />
            </template>
          </NoResults>
        </div>
      </transition>
    </div>
    <FormErrorList
      v-if="errors && (Array.isArray(errors) ? errors.length > 0 : true)"
      :errors="Array.isArray(errors) ? errors : [errors]" />
    <div v-if="$slots.hint" class="hint-text">
      <slot name="hint"></slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAttrs, computed, ref, onMounted, onBeforeUnmount } from 'vue';
import { useDropdownManager } from '@/composables/useDropdownManager';
import { IconChevronDown } from '@tabler/icons-vue';
import { FormErrorList } from '@prospective/lithium';
import translator from '@/locale/translator';
import NoResults from '@/components/form/NoResults.vue';

defineOptions({ inheritAttrs: false });

const attrs = useAttrs();
const filteredAttrs = computed(() => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { class: className, style, ...rest } = attrs;
  return rest;
});

// --- Types ---
export interface SelectOption {
  label: string;
  value: string | number;
  image?: string;
  initials?: string;
}

interface Props {
  modelValue?: string | number | null;
  label?: string;
  options: SelectOption[];
  placeholder?: string;
  errors?: string | string[] | null;
  displayInitials?: boolean;
  required?: boolean;
  emptyMessage?: string;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  placeholder: translator.get('campaigns.please_select'),
  showEntity: false,
  errors: null,
  displayInitials: false,
  required: false,
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | number): void;
}>();

const { isOpen, toggle: toggleDropdown, close: closeDropdown } = useDropdownManager();

const containerRef = ref<HTMLDivElement | null>(null);
const triggerRef = ref<HTMLDivElement | null>(null);

const selectedOption = computed(() => {
  return props.options.find((opt) => opt.value == props.modelValue);
});

const calculateInitials = (label: string): string => {
  const words = label.trim().split(/\s+/);
  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  }
  return words
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join('');
};

const getOptionInitials = (option: SelectOption | undefined): string => {
  if (!option) return '';
  if (option.initials) return option.initials;
  if (props.displayInitials) return calculateInitials(option.label);
  return '';
};

const selectOption = (option: SelectOption) => {
  emit('update:modelValue', option.value);
  closeDropdown();
  // the option unmounts on select, so focus would otherwise fall to the body
  triggerRef.value?.focus();
};

const handleClickOutside = (event: MouseEvent) => {
  if (containerRef.value && event.target instanceof Node && !containerRef.value.contains(event.target)) {
    closeDropdown();
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped lang="scss">
* {
  font-family: var(--font-brand);
}

.select-label {
  font-weight: 500;
  font-size: 13px;
  line-height: 20px;
  margin-bottom: 6px;
  color: #344054;
  user-select: none;
}

.entity-select-container {
  position: relative;
  width: 100%;
  font-family: sans-serif;
}

.select-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background-color: #fff;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  user-select: none;
  height: 40px;
  outline: none;
  outline-color: color-mix(in srgb, var(--tw-color-primary) 10%, transparent);
  box-shadow: 0px 1px 2px 0px #1018280d;

  &.has-entity {
    padding-left: 4px;
  }

  &:focus-visible {
    outline: none;
  }

  &:hover {
    border-color: var(--tblr-primary);
  }

  &:focus {
    border-color: var(--tblr-primary);
    box-shadow: 0px 1px 2px 0px #1018280d;
    outline: 4px solid color-mix(in srgb, var(--tw-color-primary) 10%, transparent);
  }

  &.is-open {
    border-color: var(--tw-color-primary);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    outline: none;
  }

  &.has-error {
    border-color: #ef4444;
  }
}

.trigger-content {
  display: flex;
  align-items: center;
  flex-grow: 1;
  overflow: hidden;
}

.placeholder-text {
  font-size: 16px;
  line-height: 24px;
  color: #1018284d;
  font-weight: 400;
}

.selected-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 16px;
  font-weight: 400;
  text-align: center;
}

/* Entity Styles */
.entity-wrapper {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  margin-right: 12px;
  border-radius: 5px;
  overflow: hidden;
  background-color: #e5e7eb;
}

.entity-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.entity-initials {
  color: #000;
  font-weight: 400;
  font-size: 13px;
  line-height: 24px;

  &.fallback {
    color: #e9e9e9;
  }
}

/* Dropdown */
.options-dropdown {
  position: absolute;
  z-index: 50;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 0;
  background-color: #fff;
  border: 1px solid var(--tw-color-primary);
  border-top: none;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  box-shadow: 0px 8px 8px 0px #00000026;
  max-height: 240px;
  overflow-y: auto;
  box-sizing: border-box;
}

.options-list {
  padding: 0;
  list-style: none;
  margin: 0;
}

.option-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  height: 42px;
  cursor: pointer;
  transition: background-color 0.15s;
  font-size: 16px;

  &.has-entity {
    padding-left: 4px;
  }

  &:hover {
    background-color: #f3f4f6;
  }

  &.is-selected {
    background-color: #eff6ff;
    color: #1d4ed8;
    font-weight: 500;
  }
}

.hint-text {
  margin-top: 6px !important;
  font-size: 12px;
  font-weight: 400;
  color: #475467;
}

.chevron-icon {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  color: #6b7280;
  transition: transform 0.2s ease;

  &.rotate-180 {
    transform: rotate(180deg);
  }
}

.fade-enter-active,
.fade-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
